import React from 'react'
import validator from 'validator'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { firebaseConnect } from 'react-redux-firebase'
import { reduxForm, formValues, Field, SubmissionError } from 'redux-form'
import { routerActions } from 'react-router-redux'
import { get } from 'lodash'
import uuid from 'uuid/v4'
import moment from 'moment'
import * as analytics from '../analytics'

import * as UI from '../components'
import * as assets from '../assets'
import * as commands from '../commands'
import * as Partials from './partials'

import { dataToArray, formatUSD, hasEarlyAccess, formatPips } from '../helpers'
import { userIsAuthenticated } from '../behaviors'

const currencyOptions = [
  { text: 'Ethereum', value: 'ETH', image: assets.ethereumLogo },
  { text: 'Bitcoin', value: 'BTC', image: assets.bitcoinLogo },
  { text: 'Stellar Lumens', value: 'XLM', image: assets.stellarLogo },
]

const getDiscountAmount = (costPerToken, originalCost) =>
  costPerToken === originalCost ? (
    ''
  ) : (
    <span style={{ paddingRight: '0.5em' }}>
      {Math.round((originalCost - costPerToken) * 100 / originalCost)}%
      discount:
    </span>
  )

const getTrancheOptions = (tranches, storefront) =>
  tranches.sort((a, b) => a.costPerToken - b.costPerToken).map(tranche => {
    const text = (
      <label>
        <UI.Text>
          {tranche.name}
          <br />
          (<UI.Text color="success" inline>
            {getDiscountAmount(tranche.costPerToken, storefront.tokenPrice)}
          </UI.Text>{' '}
          {formatPips(tranche.costPerToken)} USD/{storefront.tokenName})
        </UI.Text>
      </label>
    )
    return {
      text,
      value: tranche.key,
    }
  })

class Allocation extends React.Component {
  componentWillReceiveProps(newProps) {
    const {
      storefront: { startTime, endTime },
      profile,
      redirectToCountdown,
      redirectToClosed,
    } = newProps

    if (profile.isLoaded) {
      if (!hasEarlyAccess(profile)) {
        if (moment(startTime).isAfter(moment())) {
          redirectToCountdown()
        }
        if (moment(endTime).isBefore(moment())) {
          redirectToClosed()
        }
      }
    }
  }

  render() {
    const {
      createPurchase,
      currency,
      error,
      handleSubmit,
      invalid,
      pristine,
      profile,
      purchases,
      selectedTranche,
      storefront,
      storefrontId,
      submitFailed,
      submitSucceeded,
      submitting,
      trackTAC,
      trancheId,
      tranches,
    } = this.props

    let activePurchaseCard
    const activePurchases = dataToArray(purchases)
      .filter(purchase => {
        return (
          purchase.status === 'awaitingPayment' ||
          purchase.status === 'confirmed'
        )
      })
      .filter(purchase => {
        return purchase.sale === storefrontId
      })

    const isActivePurchase = activePurchases.length > 0

    const availableCurrencyOptions = currencyOptions.filter(x =>
      get(storefront, `currencies.${x.value}`, false)
    )

    const termsLabel = storefront.termsAndConditionsCheckboxCopy
      ? storefront.termsAndConditionsCheckboxCopy
      : `I have read and agree to the Terms and Conditions of the ${
          storefront.name
        } Sale`

    if (isActivePurchase) {
      let storefronts = {}
      storefronts[storefrontId] = storefront

      activePurchaseCard = (
        <UI.Card>
          <UI.Text uppercase color="accent">
            Complete Your Purchase
          </UI.Text>
          <UI.Divider />
          <Partials.ActivePurchasesList
            purchases={activePurchases.sort(
              (a, b) => new Date(b.initiatedTime) - new Date(a.initiatedTime)
            )}
            storefronts={storefronts}
          />
        </UI.Card>
      )
    }

    return (
      <div>
        {activePurchaseCard}

        <UI.Form onSubmit={handleSubmit(createPurchase)}>
          <UI.Card loading={submitting || submitSucceeded}>
            <UI.Image source={storefront.logoUrl} width={175} />

            <UI.Text uppercase color="accent">
              Purchase Tokens
            </UI.Text>

            <UI.Divider />

            {submitFailed && <UI.Message type="error">{error}</UI.Message>}

            <Field
              label="Select your payment currency"
              name="currency"
              component={UI.RadioField}
              options={availableCurrencyOptions}
              direction="horizontal"
              disable={[]}
            />
            {tranches && Object.keys(tranches).length > 0 ? (
              <div id="trancheSelection">
                <Field
                  fluid
                  label="How many tokens would you like to reserve?"
                  name="trancheId"
                  component={UI.RadioField}
                  options={getTrancheOptions(tranches, storefront)}
                  direction="vertical"
                />
              </div>
            ) : (
              <div>
                <UI.Text center>
                  You don't have access to any purchase options currently. Enter
                  an invitation code in the{' '}
                  <UI.Link to="/settings">settings</UI.Link> page or contact the
                  team at help@coincart.co for support.
                </UI.Text>
                <UI.Divider hidden />
              </div>
            )}

            {currency &&
              selectedTranche(trancheId) && (
                <div>
                  <UI.Message>
                    <UI.Text center>You are reserving</UI.Text>
                    <UI.Text center weight="bold" size="huge" color="success">
                      {selectedTranche(trancheId).name}
                    </UI.Text>
                    <UI.Text center weight="bold" size="huge" color="success">
                      {getDiscountAmount(
                        selectedTranche(trancheId).costPerToken,
                        storefront.tokenPrice
                      )}{' '}
                      {formatUSD(selectedTranche(trancheId).costPerToken)} USD/{
                        storefront.tokenName
                      }
                    </UI.Text>

                    <UI.Divider hidden />

                    <UI.Text center>
                      <div>Your tokens will be sent to destination address</div>
                      <strong>{profile.destinationAddress}</strong>
                    </UI.Text>
                  </UI.Message>

                  <UI.Divider hidden />
                </div>
              )}

            {storefront.termsAndConditionsLink && (
              <div>
                <UI.Link
                  to={storefront.termsAndConditionsLink}
                  target="_blank"
                  onClick={trackTAC}
                >
                  View the {storefront.name} Sale Terms and Conditions
                </UI.Link>

                <br />
                <br />
              </div>
            )}

            <Field
              label={termsLabel}
              name="termsAgreement"
              component={UI.CheckField}
            />

            <UI.Link to="https://coincart.co/terms" target="_blank">
              View the CoinCart Terms of Service
            </UI.Link>

            <br />
            <br />

            <Field
              label="I have read and agree to the CoinCart Terms of Service"
              name="ccTermsAgreement"
              component={UI.CheckField}
            />

            <UI.Divider hidden />

            <UI.Button disabled={pristine || invalid || submitting}>
              Continue to Payment &rarr;
            </UI.Button>
          </UI.Card>
        </UI.Form>
      </div>
    )
  }
}

const watches = ({ storefront }) => []

const selectors = createStructuredSelector({
  initialValues: ({ firebase }, { storefrontId }) => ({
    currency: 'ETH',
    trancheId: undefined,
    storefrontId: storefrontId,
    termsAgreement: false,
  }),
  storefront: (state, { storefront }) => storefront,
  profile: ({ firebase }) => firebase.profile,
  tranches: ({ firebase: { profile, data: { tranches } } }, { storefrontId }) =>
    dataToArray(tranches || {}).filter(tranche => {
      return tranche.storefront === storefrontId
    }),
  purchases: ({ firebase }) => firebase.data.purchases,
  selectedTranche: ({ firebase }) => trancheId =>
    (firebase.data.tranches || {})[trancheId],
})

const actions = (dispatch, props) => ({
  trackTAC: async () => {
    dispatch({ type: 'STOREFRONT_TAC_VISITED', track: {} })
  },

  createPurchase: async values => {
    try {
      const purchaseId = uuid()
      dispatch(
        routerActions.push({
          pathname: `/purchase/${purchaseId}`,
          state: { submitting: true },
        })
      )
      const authToken = await props.firebase.auth().currentUser.getIdToken(true)
      analytics.initiatePurchase({
        currency: values.currency,
        purchaseId,
        purchaseNumber: Object.keys(props.purchases || {}).length + 1,
        storefrontId: values.storefrontId,
        trancheId: values.trancheId,
        userId: (props.profile || {}).uid,
      })
      await commands.initiatePurchase({ ...values, purchaseId, authToken })

      dispatch({
        type: 'PURCHASE_INITIATED',
        track: { ...values, purchaseId },
      })
    } catch (error) {
      throw new SubmissionError({ _error: error.message })
    }
  },
  redirectToCountdown: () => dispatch(routerActions.replace('/countdown')),
  redirectToClosed: () => dispatch(routerActions.replace('/closed')),
})

const validate = values => {
  const errors = {}

  if (validator.isEmpty(values.currency || ''))
    errors.currency = 'Payment currency is required'
  if (validator.isEmpty(values.trancheId || ''))
    errors.trancheId = 'Number of tokens is required'
  if (!values.termsAgreement)
    errors.termsAgreement = `You must agree to the Terms and Conditions to participate in the token sale`
  if (!values.ccTermsAgreement)
    errors.ccTermsAgreement = 'You must agree to the CoinCart Terms of Service'

  return errors
}

export default compose(
  firebaseConnect(watches),
  connect(selectors, actions),
  reduxForm({ form: 'Allocation', validate, enableReinitialize: true }),
  formValues('currency', 'trancheId'),
  userIsAuthenticated()
)(Allocation)



// WEBPACK FOOTER //
// ./src/screens/Allocation.js